import type {} from '@mui/x-date-pickers/themeAugmentation';

import { Components, Theme } from '@mui/material';

import { typography } from '@/config/theme/typography.ts';

export const components: Components<Omit<Theme, 'components'>> = {
  MuiAccordion: {
    styleOverrides: {
      root: {
        '&:before': {
          height: 0,
        },
        '&:first-of-type, &:last-of-type': {
          borderRadius: '16px',
          margin: '10px 0',
          padding: '20px',
        },
        borderRadius: '16px',
        margin: '10px 0',
        padding: '20px',
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderTop: theme.variables.gray12Border,
        padding: '16px',
      }),
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      content: {
        '&.Mui-expanded': {
          margin: '12px 0',
        },
      },
      expandIconWrapper: ({ theme }) => ({
        backgroundColor: theme.colors.gray_10,
        borderRadius: '50px',
        padding: '6px',
      }),
      root: {
        gap: '20px',
        padding: 0,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
      },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      invisible: {
        backdropFilter: 'none',
      },
      root: {
        backdropFilter: 'blur(5px)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f12Medium,
        '&:hover:not(.Mui-disabled)': {
          boxShadow: 'none',
        },
        borderRadius: '8px',
        borderWidth: '2px !important',
        boxShadow: 'none',
        ...(ownerState.color === 'inherit' && {
          '&:hover': {
            backgroundColor: theme.colors.white,
            border: theme.variables.hoverBorder,
            color: theme.colors.blue,
          },
          border: theme.variables.gray30Border,
          color: theme.colors.gray_30,
        }),
        ...(ownerState.variant === 'contained' &&
          ownerState.color === 'primary' && {
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: theme.colors.darkBlue_65,
              boxShadow: 'none',
              color: theme.colors.white,
            },
            backgroundColor: theme.colors.darkBlue,
            color: theme.colors.white,
          }),
        ...(ownerState.variant === 'contained' &&
          ownerState.color === 'secondary' && {
            '&:hover': {
              backgroundColor: theme.colors.white,
              border: theme.variables.hoverDarkBorder,
              color: theme.colors.darkBlue,
            },
            backgroundColor: theme.colors.gray_10,
            border: theme.variables.gray10Border,
            color: theme.colors.gray_30,
          }),
        ...(ownerState.variant === 'outlined' &&
          ownerState.color === 'secondary' && {
            '&:hover': {
              backgroundColor: theme.colors.white,
              border: theme.variables.hoverDarkBorder,
              color: theme.colors.darkBlue,
            },
            backgroundColor: theme.colors.white,
            border: theme.variables.gray12Border,
            color: theme.colors.gray_30,
          }),
        ...(ownerState.variant === 'outlined' &&
          ownerState.color === 'inherit' && {
            '.MuiAvatar-root': {
              backgroundColor: theme.colors.blue,
              borderRadius: '8px',
              color: 'black',
              height: '30px',
              paddingRight: 2,
              width: '30px',
            },
            '&:hover': {
              '.MuiAvatar-root': {
                backgroundColor: theme.colors.darkBlue,
                color: theme.colors.white,
              },
              backgroundColor: theme.colors.gray_10,
              border: theme.variables.gray12Border,
              color: theme.colors.darkBlue,
            },
            backgroundColor: theme.colors.white,
            border: theme.variables.gray12Border,
            color: theme.colors.gray_30,
            justifyContent: 'flex-start',
            textTransform: 'none',
          }),
      }),
      text: ({ theme }) => ({
        '&:hover:not(.Mui-disabled)': {
          backgroundColor: 'transparent',
          color: theme.colors.blue,
        },
        backgroundColor: 'transparent',
        color: theme.colors.black_65,
        textTransform: 'none',
      }),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.colors.gray_18,
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      label: ({ ownerState, theme }) => ({
        ...theme.typography.f12Medium,
        ...(ownerState.size === 'small' && {
          padding: '4px',
          ...theme.typography.f12Medium,
        }),
      }),
      root: ({ ownerState, theme }) => ({
        borderRadius: '4px',
        height: '24px',
        paddingBottom: '4px',
        paddingTop: '4px',
        ...(ownerState.color === 'darkBlue' && {
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
        }),
        ...(ownerState.color === 'orange' && {
          backgroundColor: theme.colors.orange,
          color: theme.colors.white,
        }),
        ...(ownerState.color === 'gray' && {
          backgroundColor: theme.colors.gray_20,
          color: theme.colors.white,
        }),
        ...(ownerState.color === 'red' && {
          backgroundColor: theme.colors.red,
          color: theme.colors.white,
        }),
        ...(ownerState.color === 'lightOrange' && {
          backgroundColor: theme.colors.lightOrange,
          color: theme.colors.black,
        }),
        ...(ownerState.color === 'yellow' && {
          backgroundColor: theme.colors.yellow,
          color: theme.colors.black,
        }),
        ...(ownerState.color === 'blue' && {
          backgroundColor: theme.colors.blue,
          color: theme.colors.black,
        }),
        ...(ownerState.color === 'lightGreen' && {
          backgroundColor: theme.colors.lightGreen,
          color: theme.colors.black,
        }),
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '16px',
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      input: ({ ownerState, theme }) => ({
        padding: '25px 12px 12px',
        ...(ownerState.hiddenLabel && {
          padding: '12px 3px 12px',
        }),
        ...(ownerState.size === 'small' && {
          ...typography.f12Regular,
          height: '20px',
          padding: '10px 8px 0 0',
        }),
        ...(ownerState.multiline && {
          padding: '0',
        }),
        '&.Mui-disabled': {
          opacity: 1,
          WebkitTextFillColor: theme.colors.gray_20,
        },
      }),
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f14Regular,
        '&::after': {
          border: 'none !important',
        },
        '&::before': {
          border: 'none !important',
        },
        '&:hover:not(.Mui-disabled), &:hover:not(.Mui-error), &.Mui-focused': {
          backgroundColor: theme.colors.white,
          border: theme.variables.hoverBorder,
          borderWidth: '2px',
        },
        '&.Mui-disabled': {
          '&:hover': {
            borderColor: theme.colors.gray_10,
          },
          backgroundColor: theme.colors.gray_10,
          color: theme.colors.gray_20,
        },
        '&.Mui-error': {
          '&:hover': {
            borderColor: theme.colors.red,
          },
          borderColor: theme.colors.red,
        },
        backgroundColor: theme.colors.white,
        border: theme.variables.whiteBorder,
        borderRadius: '8px',
        borderWidth: '2px',
        color: theme.colors.black,
        paddingLeft: '10px',
        transform: 'none',
        ...(ownerState.color === 'secondary' && {
          '&:hover:not(.Mui-disabled), &:hover:not(.Mui-error), &.Mui-focused': {
            backgroundColor: theme.colors.gray_10,
            border: theme.variables.hoverBorder,
            borderWidth: '2px',
          },
          backgroundColor: theme.colors.gray_10,
        }),
      }),
      sizeSmall: {
        height: '44px',
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        '& .MuiPickersInputBase-root:hover .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        background: 'white',
        borderRadius: 8,
        marginTop: '0px !important',
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      root: ({ theme }) => ({
        '&.Mui-error': {
          color: theme.colors.red,
        },
        color: theme.colors.black,
        fontSize: '16px',
        fontWeight: 600,
        marginBottom: '4px',
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderRadius: '12px',
        padding: '4px',
        ...(ownerState.variant === 'contained' &&
          ownerState.color === 'primary' && {
            '&:hover': {
              backgroundColor: theme.colors.blue,
            },
            backgroundColor: theme.colors.gray_15,
            color: theme.colors.black,
          }),
        ...(ownerState.variant === 'contained' &&
          ownerState.color === 'secondary' && {
            '&:hover': {
              backgroundColor: theme.colors.white,
              border: theme.variables.hoverDarkBorder,
              borderWidth: '2px',
              color: theme.colors.darkBlue,
            },
            backgroundColor: theme.colors.white,
            border: theme.variables.gray12Border,
            borderWidth: '2px',
            color: theme.colors.gray_30,
          }),
        ...(ownerState.variant === 'ghost' && {
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.colors.blue,
          },
          backgroundColor: 'transparent',
          color: theme.colors.gray_20,
        }),
        ...(ownerState.variant === 'outlined' && {
          '&:hover': {
            backgroundColor: 'transparent',
            border: theme.variables.hoverBorder,
            borderWidth: '2px',
            color: theme.colors.blue,
          },
          backgroundColor: 'transparent',
          border: theme.variables.gray12Border,
          borderWidth: '2px',
        }),
      }),
      sizeMedium: {
        padding: '8px',
      },
      sizeSmall: {
        borderRadius: '8px',
        padding: '6px',
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          ...theme.typography.f14Regular,
          color: theme.colors.gray_20,
        },
        padding: 0,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&::after': {
          border: 'none !important',
        },
        '&::before': {
          border: 'none !important',
        },
        backgroundColor: 'transparent',
        border: theme.variables.whiteBorder,
        borderColor: 'transparent',
        borderRadius: '8px',
        padding: 0,
      }),
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.colors.gray_20,
        marginRight: '0',
        marginTop: '10px !important',
        svg: {
          height: '0.6em',
          width: '0.6em',
        },
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      outlined: {
        transform: 'translate(12px, 4px) scale(0.75)',
      },
      root: ({ ownerState, theme }) => ({
        '&.Mui-focused': {
          color: theme.colors.gray_20,
        },
        color: theme.colors.gray_20,
        fontSize: '12px',
        ...(ownerState.size !== 'small' && {
          fontSize: '14px',
        }),
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      bar: ({ theme }) => ({
        backgroundColor: theme.colors.blue,
        borderRadius: '100px',
      }),
      root: ({ theme }) => ({
        backgroundColor: theme.colors.lightGrey,
        borderRadius: '100px',
        height: '8px',
      }),
    },
  },
  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.colors.darkBlue,
        cursor: 'pointer',
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      list: {
        padding: '8px',
      },
      paper: ({ theme }) => ({
        backgroundColor: theme.colors.white,
        backgroundImage: 'none',
        border: theme.variables.gray12Border,
        borderRadius: '12px',
        boxShadow: theme.variables.boxShadowMenu,
        marginTop: '6px',
        minWidth: '220px',
        overflow: 'hidden',
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f12Medium,
        '&:hover': {
          backgroundColor: theme.colors.gray_10,
          color: theme.colors.darkBlue,
        },
        '&:not(:last-of-type)': {
          marginBottom: '4px',
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
        '&.Mui-selected': {
          backgroundColor: theme.colors.lightBlue,
          color: theme.colors.darkBlue,
        },
        '&.Mui-selected:hover': {
          backgroundColor: theme.colors.blue_10,
        },
        borderRadius: '8px',
        color: theme.colors.gray_30,
        minHeight: '40px',
        padding: '10px 12px',
        transition: theme.variables.basicTransition,
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: ({ ownerState }) => ({
        padding: '25px 12px 12px',
        ...(ownerState.size === 'small' && {
          ...typography.f12Regular,
          height: '20px',
          padding: '10px 8px 0 10px',
        }),
      }),
      root: ({ theme }) => ({
        ...typography.f14Regular,
        '.MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&:hover': {
          '.MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          border: theme.variables.hoverBorder,
          borderWidth: '2px',
        },
        border: theme.variables.gray12Border,
        borderRadius: '8px',
        borderWidth: '2px',
        // paddingLeft: '10px',
      }),
      sizeSmall: {
        height: '40px',
      },
    },
  },
  MuiPagination: {
    styleOverrides: {
      ul: {
        gap: '15px',
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f12Regular,
        '&:hover:not(.Mui-focused)': {
          backgroundColor: theme.colors.gray_20,
        },
        '&.Mui-disabled': {
          color: theme.colors.silver,
          opacity: 1,
        },
        '&.Mui-selected': {
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
        },
        '&.Mui-selected:hover:not(.Mui-focused)': {
          backgroundColor: theme.colors.darkBlue_65,
        },
        backgroundColor: theme.colors.white,
        color: theme.colors.black,
        height: '30px',
        padding: '6px 10px',
        width: '30px',
        ...(ownerState.color === 'secondary' && {
          backgroundColor: theme.colors.gray_10,
        }),
      }),
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      // @ts-expect-error: prop exists, type issue?
      section: { fontSize: '12px', paddingTop: 8 },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:active': { border: `2px solid ${theme.colors.blue}` },
        '&:hover': { border: `2px solid ${theme.colors.blue_65}` },
        border: `2px solid ${theme.colors.gray_12}`,
        borderRadius: '8px',
      }),
      sectionsContainer: { padding: '8px 0 4px 0' },
    },
  },
  MuiPickersTextField: {
    styleOverrides: {},
  },
  MuiRating: {
    styleOverrides: {
      iconHover: ({ theme }) => ({
        color: theme.colors.blue,
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        backgroundColor: theme.colors.white,
        padding: '4px 6px',
        ...(ownerState.color === 'secondary' && {
          backgroundColor: theme.colors.gray_10,
        }),
      }),
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiSlider-mark': {
          borderRadius: '3px',
          color: theme.colors.darkBlue,
          height: '10px',
          opacity: 1,
          top: '15px',
          transform: 'rotate(45deg)',
          width: '10px',
        },
        '& .MuiSlider-markActive': {
          backgroundColor: theme.colors.blue,
        },
        '& .MuiSlider-markLabel': {
          paddingLeft: '10px',
        },
        '& .MuiSlider-rail': {
          color: theme.colors.darkBlue,
          height: '4px',
          opacity: 1,
        },
        '& .MuiSlider-thumb': {
          '&:hover': {
            boxShadow: '0 0 0 8px rgba(58, 133, 137, 0.16)',
          },
          '& .slider-mark': {
            backgroundColor: theme.colors.blue,
            borderRadius: '6px',
            height: '23px',
            textAlign: 'center',
            transform: 'rotate(45deg)',
            width: '23px',
          },
          backgroundColor: 'transparent',
          border: '1px solid transparent',
          height: '20px',
          width: '24px',
        },
        '& .MuiSlider-track': {
          height: '4px',
        },
        color: theme.colors.blue,
        height: '40px',
        padding: '0',
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        borderRadius: '20px',
        minHeight: '20px',
        padding: '12px',
      }),
      textColorInherit: ({ theme }) => ({
        '&.Mui-selected': {
          backgroundColor: theme.colors.blue,
          borderRadius: '10px',
          color: theme.colors.white,
        },
        borderRadius: '8px',
        height: '100%',
      }),
      textColorSecondary: ({ theme }) => ({
        '&.Mui-selected': {
          backgroundColor: theme.colors.blue,
        },
        backgroundColor: theme.colors.white,
        border: 'none',
        borderRadius: '50px',
        color: theme.colors.black,
        fill: theme.colors.black,
        minHeight: '20px',
        padding: '12px',
      }),
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        tableLayout: 'fixed',
        width: '100%',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: ({ theme }) => ({
        ...theme.typography.f11Medium, // f11Regular Mockups
        '&:first-of-type': {
          paddingLeft: '25px',
          paddingRight: '25px',
        },
        '&:last-of-type': {
          paddingRight: '32px',
        },
        padding: '12px',
      }),
      root: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      sizeMedium: ({ theme }) => ({
        ...theme.typography.f11Regular, // f11Regular Mockups
        '&:first-of-type': {
          paddingLeft: '32px',
        },
        '&:last-of-type': {
          paddingRight: '32px',
        },
        padding: '16px 12px',
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: theme.variables.boxShadowTableHead,
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...(ownerState.clickable && {
          '&:hover': {
            backgroundColor: theme.colors.blue_10,
            cursor: 'pointer',
          },
        }),

        '&:last-of-type > td': {
          border: 'none',
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      flexContainer: {
        height: '100%',
      },
      indicator: ({ ownerState, theme }) => ({
        ...(ownerState.indicatorColor === 'secondary' && {
          display: 'none',
        }),
        backgroundColor: 'transparent',
        borderBottom: theme.variables.hoverBorder,
        borderRadius: '8px',
        borderWidth: '2px',
        minHeight: '25px',
      }),
      root: ({ ownerState, theme }) => ({
        minHeight: '20px',
        ...(ownerState.color === 'primary' && {
          alignItems: 'center',
          backgroundColor: theme.colors.gray_10,
          borderRadius: '8px',
        }),
        ...(ownerState.color === 'invalid' && {
          alignItems: 'center',
          backgroundColor: theme.colors.gray_10,
          border: theme.variables.invalidBorder,
          borderRadius: '8px',
        }),
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiToggleButtonGroup-grouped': {
          border: `1px solid ${theme.colors.gray_12}`,
          borderRadius: '4px',
          color: theme.colors.gray_30,
          margin: 0,
          padding: '6px 10px',
        },
        '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
          borderLeft: `1px solid ${theme.colors.gray_12}`,
          marginLeft: 0,
        },
        '& .MuiToggleButtonGroup-grouped.Mui-selected': {
          '&:hover': {
            backgroundColor: theme.colors.turquoise,
          },
          backgroundColor: theme.colors.turquoise,
          borderColor: theme.colors.turquoise,
          boxShadow: `inset 0 0 0 1px ${theme.colors.turquoise}`,
          color: theme.colors.white,
          fontWeight: 400,
        },
        gap: '4px',
        height: '38px',
      }),
    },
  },
  MuiToolbar: {
    styleOverrides: {
      dense: {
        margin: 0,
        minHeight: '40px',
        padding: 0,
      },
    },
  },
};
